import { REGION_RESTRICTED_MESSAGE, SERVER_TROUBLE_MESSAGE } from 'constants/locationMessages';

/**
 * Pure helpers for the location-gating flow.
 *
 * Serves BOTH implementations. `isFreshLoginTransition` and
 * `GEOLOCATION_OPTIONS` are used by the LexisNexis path; `shouldRunGeolocation`
 * and `computeLocationEventUpdates` are used by the legacy geoFindMe /
 * GOT_LOCATION path, which the `ops.client-geofence-path` flag can select.
 *
 * The two legacy helpers were deleted when LexisNexis replaced that path and
 * restored verbatim when the fallback was built. They are NOT dead code: they
 * are what the kill switch runs.
 *
 * `shouldRetryLocationCheck` and `RESUME_RECHECK_THROTTLE_MS` were also once
 * dropped here, on the reasoning that the resume re-check "was removed on its
 * own merits and the fallback does not need it". EN-11114 showed that was
 * wrong, and wrong specifically for the LexisNexis path rather than the
 * fallback: the vendor path runs its check ONCE per login, from an effect whose
 * dependencies do not move when a location permission changes. A player who
 * launches with permission denied, grants it in Settings and returns therefore
 * never gets re-checked. On native that is the only way to grant permission, so
 * there is no recovery at all -- `$connect` keeps refusing a verdict that was
 * never rewritten, and a refused socket is indistinguishable from a dead
 * network to the client.
 *
 * The legacy path did not have that hole because it carried exactly these two
 * helpers. They are restored unchanged.
 *
 * Related RCA: docs/investigations/20260728-invalid-location-modal-stuck-on-app-resume.md
 */

/**
 * Whether `geoFindMe` should proceed with a `getCurrentPosition` call on this
 * tick, or skip because the app / tab is currently backgrounded.
 *
 * Skipping while backgrounded avoids the wall-clock-timeout race: the browser's
 * geolocation timeout is measured in real time, not foreground time. Starting a
 * 10-30 s timer as the user backgrounds the app is guaranteed to reject with
 * code 3 (TIMEOUT) by the time they return - even when permission was granted
 * and GPS was fine. Deferring here lets the `visibilitychange` / Capacitor
 * `appStateChange` listener re-invoke the check once we're foregrounded again.
 *
 * `undefined` is treated as "run" so this stays safe under SSR / non-DOM
 * environments and in the initial-mount ordering where `document` may not have
 * a visibilityState set yet.
 */
export function shouldRunGeolocation(visibilityState: string | undefined): boolean {
  if (visibilityState === undefined) return true;
  return visibilityState === 'visible';
}

/**
 * State updates to apply for a given `GOT_LOCATION` socket event status.
 * `undefined` (field absent) means "do not touch this setter". Keeping it a
 * pure data function lets callers apply the updates however they like
 * (setState, dispatch, etc.) and lets tests assert the exact set of touched
 * flags per branch.
 */
export type LocationEventUpdates = {
  isLocated?: boolean;
  isLocationInvalid?: boolean;
  invalidLocationMsg?: string;
};

/**
 * Given a `GOT_LOCATION` event's `status` value, decide the state updates:
 *
 * - `success` - SIMULTANEOUSLY flip `isLocated=true`, `isLocationInvalid=false`,
 *   `invalidLocationMsg=''`. This is the ONE authoritative path that closes
 *   the Region-Blocked modal; the previous implementation only set
 *   `isLocated=true`, which left the modal stuck the moment it had ever been
 *   raised earlier in the session.
 * - `error` - flip `isLocationInvalid=true`. Deliberately does NOT touch
 *   `invalidLocationMsg`: geoFindMe's error branch may have already set a
 *   specific per-code message (permission denied / timeout / etc.) and this
 *   handler shouldn't clobber it.
 * - anything else - no updates (future-proofs against server variants).
 */
/**
 * The engine's `description` values, which are the only thing that says WHY.
 *
 * `status` is just success/error, so every refusal used to render as
 * InvalidLocationModal's default copy -- which talks about granting location
 * permission. A player refused for being outside an allowed region was
 * therefore told to go check a permission that was already granted and could
 * not have helped, while the one fact that would have explained it was
 * discarded here. It cost most of a day of debugging on test2 (2026-10-10), on
 * a player whose permission was granted throughout.
 *
 * AN ALLOWLIST, matching `messageForRefusal` on the vendor path and for the
 * same reason: an unrecognised description must not be turned into a confident
 * claim about the player's location. Anything not listed leaves
 * `invalidLocationMsg` untouched, which preserves the previous behaviour
 * exactly -- including geoFindMe's per-code messages, which are more specific
 * than anything derivable here and must not be clobbered.
 */
const REGION_REFUSAL_DESCRIPTIONS = new Set(['Location is invalid.']);

/** Our own lookup or write failed. Says nothing about where the player is. */
const SERVER_TROUBLE_DESCRIPTIONS = new Set([
  'Player information not found.',
  'There was an issue storing location information. Please consult your administrator'
]);

export function computeLocationEventUpdates(
  status: string | undefined,
  description?: string
): LocationEventUpdates {
  if (status === 'success') {
    return { isLocated: true, isLocationInvalid: false, invalidLocationMsg: '' };
  }
  if (status === 'error') {
    if (description && REGION_REFUSAL_DESCRIPTIONS.has(description)) {
      return { isLocationInvalid: true, invalidLocationMsg: REGION_RESTRICTED_MESSAGE };
    }
    if (description && SERVER_TROUBLE_DESCRIPTIONS.has(description)) {
      return { isLocationInvalid: true, invalidLocationMsg: SERVER_TROUBLE_MESSAGE };
    }
    // `Problem with getting player location.` lands here on purpose: that IS a
    // position/permission failure, so the modal's default copy is correct, and
    // geoFindMe's own per-code message is better still.
    return { isLocationInvalid: true };
  }
  return {};
}

/**
 * Whether a resume-triggered re-check should proceed, or short-circuit.
 *
 * Order matters:
 *   1. Guest gate (`guid === ''`) -- matches the "guests do not check location"
 *      spec. A tabbing guest has no login-bound location to verify; skipping
 *      here also stops us uselessly cycling `isLocated` / `retryTick`.
 *   2. Modal-showing shortcut -- if the modal is currently up, always retry.
 *      A transient failure should recover on the very next foreground event.
 *      THIS IS THE CASE EN-11114 TURNS ON: the player went to Settings to grant
 *      the permission that raised the modal, so the one foreground event most
 *      likely to clear it is the one the throttle would otherwise swallow.
 *   3. Otherwise -- throttle to `throttleMs` between checks so a rapid tab-
 *      switcher does not fire a vendor lookup on every focus. On the vendor
 *      path that throttle is also a spend control, not just a battery one:
 *      every un-throttled re-check that reaches the vendor is billed.
 */
export function shouldRetryLocationCheck(input: {
  guid: string;
  isLocationInvalid: boolean;
  now: number;
  lastCheckAt: number;
  throttleMs: number;
}): boolean {
  const { guid, isLocationInvalid, now, lastCheckAt, throttleMs } = input;
  if (guid === '') return false;
  if (isLocationInvalid) return true;
  return now - lastCheckAt >= throttleMs;
}

/**
 * True when the tracked `guid` transition is a fresh guest → logged-in login
 * (empty string → non-empty string). Used to trigger the state-hygiene wipe of
 * any location-modal state carried over from the guest phase: the Lobby
 * component doesn't unmount across the login transition, so an out-of-order
 * `GOT_LOCATION` event or a stray retry-tick from the guest phase could
 * otherwise render a false-positive modal the moment the user logs in.
 *
 * Deliberately returns false on:
 *   - logout (real → empty)
 *   - already-logged-in re-render (same real → same real)
 *   - guid-swap between two logged-in states (real → different real)
 *   - guest-only tick (empty → empty)
 */
export function isFreshLoginTransition(prev: string, next: string): boolean {
  return prev === '' && next !== '';
}

/**
 * `getCurrentPosition` options. No longer used for gating — the vendor owns
 * that — but still used by warmLocationProvider and the WebView probe on native
 * Geolocation) and web (`navigator.geolocation`). Exported so tests can lock in
 * the numeric values that the RCA depends on:
 *
 * - `timeout: 30_000` — cold-GPS on a fresh native install needs 15-25 s to
 *   acquire the first satellite fix. The prior 10 s ceiling caused a false-
 *   positive "attempt timed out" modal on the very first login after install
 *   even when location permission was granted.
 * - `maximumAge: 0` — no cached positions. Region gating is regulatory and a
 *   cached position could be used to bypass a fresh check. Also fixes the
 *   "sensor changed but the browser kept returning the old Tokyo coords"
 *   flakiness during DevTools testing.
 * - `enableHighAccuracy: false` — coarse position is sufficient for region
 *   gating and avoids the extra battery / time cost of enabling the GPS
 *   high-accuracy mode.
 */
export const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 30_000,
  maximumAge: 0
} as const;

/**
 * Throttle window between resume-triggered location re-checks when the modal is
 * NOT currently showing. Exported so `Lobby.tsx` and its tests share one source
 * of truth.
 *
 * Bypassed entirely while the modal is up; see `shouldRetryLocationCheck`.
 */
export const RESUME_RECHECK_THROTTLE_MS = 30_000;
